import React, { useMemo, useState } from 'react';
import { usePageMeta, organizationSchema, breadcrumbSchema } from '../lib/seo';
import {
  ButtonLink,
  Card,
  Container,
  CTASection,
  FeatureList,
  PageHero,
  Section,
} from '../components/SiteUi';

const currency = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});

export default function CalculatorPage() {
  const [calls, setCalls] = useState(40);
  const [missed, setMissed] = useState(20);
  const [jobValue, setJobValue] = useState(350);

  const result = useMemo(() => {
    const weeklyLost = Math.max(0, Number(calls) || 0) * (Math.max(0, Math.min(100, Number(missed) || 0)) / 100);
    const monthlyLost = weeklyLost * 4.33 * Math.max(0, Number(jobValue) || 0);
    return {
      weeklyCalls: weeklyLost,
      monthlyLost,
      annualLost: monthlyLost * 12,
    };
  }, [calls, missed, jobValue]);

  usePageMeta({
    title: 'Missed Call Cost Calculator – CloudNext',
    description: 'Estimate what missed calls could be costing your trade or service business each month.',
    path: '/tools/missed-call-calculator',
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Missed Call Calculator', path: '/tools/missed-call-calculator' }])],
  });

  return (
    <>
      <PageHero
        eyebrow="Free tool"
        title={<>What could missed calls be <span className="text-gold-gradient">costing you?</span></>}
        description="A simple estimate. Change the numbers to match your business. It is a planning tool, not a guarantee."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Missed Call Calculator' }]}
      />

      <Section>
        <Container>
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <Card>
              <h2 className="font-display text-2xl text-cream">Your numbers</h2>
              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="calls" className="mb-2 block text-sm text-dust">Calls per week</label>
                  <input id="calls" type="number" min="0" value={calls} onChange={(event) => setCalls(event.target.value)} className="w-full rounded-xl border border-white/[0.08] bg-ink-light/70 px-4 py-3 text-cream outline-none focus:border-gold" />
                </div>
                <div>
                  <label htmlFor="missed" className="mb-2 block text-sm text-dust">Percentage you miss or cannot answer</label>
                  <input id="missed" type="number" min="0" max="100" value={missed} onChange={(event) => setMissed(event.target.value)} className="w-full rounded-xl border border-white/[0.08] bg-ink-light/70 px-4 py-3 text-cream outline-none focus:border-gold" />
                </div>
                <div>
                  <label htmlFor="jobValue" className="mb-2 block text-sm text-dust">Average job value (AUD)</label>
                  <input id="jobValue" type="number" min="0" value={jobValue} onChange={(event) => setJobValue(event.target.value)} className="w-full rounded-xl border border-white/[0.08] bg-ink-light/70 px-4 py-3 text-cream outline-none focus:border-gold" />
                </div>
              </div>
              <p className="mt-5 text-xs leading-5 text-ash">
                This assumes every missed call is a lost job. In reality, not every call converts. Use it as a rough planning estimate.
              </p>
            </Card>

            <div className="space-y-4">
              <div className="rounded-3xl border border-gold/20 bg-gold/[0.06] p-6 md:p-8">
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">Revenue you could be losing</p>
                <p className="mt-3 font-display text-4xl text-cream md:text-5xl">{currency.format(result.monthlyLost)}</p>
                <p className="mt-2 text-sm text-dust">per month</p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/[0.06] bg-ink/30 p-4">
                    <p className="text-xs text-ash">Missed calls per week</p>
                    <p className="mt-1 font-display text-xl text-cream">{result.weeklyCalls.toFixed(1)}</p>
                  </div>
                  <div className="rounded-2xl border border-white/[0.06] bg-ink/30 p-4">
                    <p className="text-xs text-ash">Estimated per year</p>
                    <p className="mt-1 font-display text-xl text-cream">{currency.format(result.annualLost)}</p>
                  </div>
                </div>
              </div>
              <Card>
                <h3 className="font-display text-xl text-cream">What to do with this number</h3>
                <FeatureList
                  className="mt-4"
                  items={[
                    'Check how many calls actually go to voicemail.',
                    'Look at after-hours calls and busy-time calls separately.',
                    'Decide whether the fix is an AI receptionist, a message service, or better call handling.',
                    'Compare this with what a fix would cost.',
                  ]}
                />
                <ButtonLink to="/contact" className="mt-5">Talk through the numbers</ButtonLink>
              </Card>
            </div>
          </div>
        </Container>
      </Section>

      <CTASection title="Want to stop the leak?" description="We can set up a tested AI receptionist, or tell you if a simpler fix is enough." primaryTo="/ai-receptionist" primaryLabel="See the AI receptionist" secondaryTo="/contact" secondaryLabel="Book a call" />
    </>
  );
}
